import { ArrowUpRight, CheckCircle2, Circle, FlaskConical, KeyRound, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import type { Campaign } from '../../api/types';
import { FlowStep } from '../../components/setup/SetupKit';
import { identityLine } from '../../lib/channelMeta';
import { useLoad } from '../../lib/useLoad';
import { openSettingsTab } from './settingsTab';
import { useComms } from './context';
import { useCampaignSetup } from './useCampaignSetup';
import { useConfigNav } from './useConfigNav';

/**
 * The Campaigns tab's Guide: how a campaign goes from nothing to a report, laid
 * out as the steps it is done in, each answered from this tenant's own state.
 *
 * The same shape the Analytics install and the Chatbot install screens use —
 * numbered steps on the left, what is left to do on the right — because those
 * are the two screens an operator has already learnt this from. Every step
 * reads something real rather than ticking itself when visited: whether a
 * sender is connected, whether a mailbox's records were checked, whether the
 * website is known to us and counted, whether anything has gone out. A guide
 * whose ticks are decoration teaches people to ignore ticks.
 *
 * The website step is what links this tab to Analytics: a campaign's links to
 * the tenant's own site carry its tracking name, and the visits and bookings
 * they bring are read back on Results and on the Analytics Campaigns card.
 */
export function CampaignGuide({
  campaigns,
  onNewCampaign,
  onTryTest,
  onOpenCampaign,
  onOpenAnalytics,
}: {
  /** The list's first page, newest first — enough to know whether anything has gone out. */
  campaigns: Campaign[];
  onNewCampaign: () => void;
  /** The builder with the step-by-step walkthrough, addressed to just this person. */
  onTryTest: () => void;
  onOpenCampaign: (campaign: Campaign) => void;
  onOpenAnalytics?: () => void;
}) {
  const { source, capabilities, sendDenied, reloadInboxes } = useComms();
  const configNav = useConfigNav();
  // A new tab, with Check again beside it — see settingsTab.ts.
  const connect = () => openSettingsTab('/connect', source.embedded);
  const features = capabilities?.access.features;
  // Permissive while /meta is in flight, like every capability read on this module.
  const level = features?.campaigns ?? 'full';
  const mayDraft = (level === 'draft' || level === 'full') && !sendDenied;
  const mayConnect = capabilities?.access.features.connectChannels ?? false;
  const analyticsOn = (features?.analytics ?? 'view') !== 'off';
  const aiOffered = (features?.ai ?? 'use') !== 'off';
  const mayManageAi = features?.ai === 'manage';
  // Re-asked on Check again, for whoever went off to add a key in another tab.
  const [aiProbe, setAiProbe] = useState(0);
  const ai = useLoad(
    () => (aiOffered ? source.api.getAiAvailability() : Promise.resolve(null)),
    [source, aiOffered, aiProbe],
  );
  const aiReady = ai.data?.available ?? null;

  const {
    tracking,
    sites,
    mailboxes,
    senders,
    managedMailboxes,
    checkedMailbox,
    hosts,
    webInstalls,
    reporting,
    websiteReady,
  } = useCampaignSetup();

  const sent = campaigns.find((campaign) => campaign.status !== 'draft' && campaign.startedAt);
  const tracked = campaigns.some((campaign) => campaign.utmCampaign);

  const steps = [
    { key: 'sender', label: 'Connect where it sends from', done: senders.length > 0 },
    // Only asked of a tenant that mails: a texts-only operator has no mailbox to check.
    ...(mailboxes.length ? [{ key: 'health', label: 'Check the mailbox before a big send', done: checkedMailbox }] : []),
    { key: 'website', label: 'Count the visits on your website', done: websiteReady },
    { key: 'send', label: 'Write it, pick who gets it, send', done: Boolean(sent) },
  ];
  const doneCount = steps.filter((step) => step.done).length;
  // The numbered order on the page. Wider than the checklist: AI is optional and
  // reading the results comes after, so neither is something left to DO.
  const order = [
    'sender',
    ...(mailboxes.length ? ['health'] : []),
    'website',
    ...(aiOffered ? ['ai'] : []),
    'send',
    'results',
  ];
  const n = (key: string) => order.indexOf(key) + 1;
  // By position: the steps are drawn in this list's order, and FlowStep is the
  // settings kit's own and carries no id to aim at.
  const go = (key: string) => {
    const step = document.querySelectorAll('.cmg-guide .an-flow-step')[n(key) - 1];
    step?.scrollIntoView({ block: 'start', behavior: 'auto' });
  };

  return (
    <div className="cmg-guide">
      <header className="cmg-guide-head">
        <h2>How campaigns work</h2>
        <p>
          A campaign is one email or text to many people at once — customers, affiliates and chauffeurs
          straight from dispatch, or a list of your own. Replies land in the inbox like any other
          conversation, and with website tracking on, the visits and reservations each campaign brings are
          counted for it.
        </p>
        {mayDraft && senders.length > 0 ? (
          <div className="cmg-guide-try">
            <button type="button" className="btn btn-primary" onClick={onTryTest}>
              <FlaskConical size={14} />
              Try a test campaign
            </button>
            <span>
              Walks the builder once, a step at a time, and sends it to just you — the safest way to
              see what your customers will get.
            </span>
          </div>
        ) : mayDraft ? (
          /* Nothing to send from: the first thing to do is connect, and the
             walkthrough is still there to learn the builder by — it says on its
             own first steps that it cannot send yet. */
          <div className="cmg-guide-try">
            {mayConnect ? (
              <button type="button" className="btn btn-primary" onClick={connect}>
                <Plus size={14} />
                Connect a mailbox or number
                <ArrowUpRight size={14} />
              </button>
            ) : null}
            <span>
              {mayConnect
                ? 'Start here: a campaign goes out from a mailbox or number connected to the CRM, and none is yet. '
                : 'A campaign goes out from a mailbox or number connected to the CRM, and none you can send from is yet — an administrator connects one. '}
              You can still walk through the builder to see how it works.
            </span>
            <button type="button" className="btn btn-secondary" onClick={onTryTest}>
              <FlaskConical size={14} />
              Walk through it
            </button>
          </div>
        ) : null}
      </header>

      <div className="an-setup">
        <ol className="an-flow">
          <FlowStep
            n={n('sender')}
            done={senders.length > 0}
            title="Connect where it sends from"
            tag={senders.length ? { label: 'Done', tone: 'ok' } : { label: 'Start here', tone: 'accent' }}
          >
            <p>
              Email goes out from a mailbox connected here — Gmail, Microsoft 365 or any SMTP mailbox. Texts
              go out from a Twilio or RingCentral number. Either way it leaves from an address your
              customers already know, and their replies come back to it.
            </p>
            {senders.length ? (
              <p className="cmg-guide-list">
                Ready to send from {senders.slice(0, 3).map(identityLine).join(', ')}
                {senders.length > 3 ? ` and ${senders.length - 3} more` : ''}.
              </p>
            ) : mayConnect ? (
              <div className="cmg-guide-actions">
                <button type="button" className="btn btn-primary" onClick={connect}>
                  <Plus size={14} />
                  Connect a mailbox or number
                  <ArrowUpRight size={14} />
                </button>
                <button type="button" className="btn btn-ghost" onClick={reloadInboxes}>
                  Check again
                </button>
              </div>
            ) : (
              <p className="cmg-guide-list">
                Nothing is connected that you can send from. Ask an administrator to connect a mailbox or
                number — or to give you access to one that is.
              </p>
            )}
          </FlowStep>

          {mailboxes.length > 0 && (
                  <FlowStep
                n={n('health')}
                done={checkedMailbox}
                title="Check the mailbox before a big send"
                tag={checkedMailbox ? { label: 'Done', tone: 'ok' } : { label: 'Recommended', tone: 'accent' }}
              >
                <p>
                  Gmail, Yahoo and Outlook file bulk mail as spam when the sending domain has no SPF, DKIM
                  and DMARC records, or when the address is brand new. Each mailbox’s settings read both
                  and suggest a daily cap that warms a new address up gently. Campaigns stay under that cap
                  on their own, and always leave part of the day’s allowance for replies.
                </p>
                {managedMailboxes.length ? (
                  <div className="cmg-guide-actions">
                    {managedMailboxes.slice(0, 3).map(({ inbox, health }) => (
                      <button
                        key={inbox.id}
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => configNav(`/inboxes/${inbox.id}`)}
                      >
                        <ShieldCheck size={14} />
                        {identityLine(inbox)}
                        <span className={`cmg-guide-pill is-${health?.level ?? 'unknown'}`}>
                          {!health
                            ? 'not checked'
                            : health.level === 'ok'
                              ? 'healthy'
                              : health.level === 'unknown'
                                ? 'could not check'
                                : 'needs a look'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="cmg-guide-list">
                    Whoever manages your mailboxes can read this on each one’s settings.
                  </p>
                )}
              </FlowStep>
          )}

          <FlowStep
            n={n('website')}
            done={websiteReady}
            title="Count the visits on your website"
            tag={websiteReady ? { label: 'Done', tone: 'ok' } : { label: 'Recommended', tone: 'accent' }}
          >
            <p>
              Leave <b>Website tracking</b> on in the builder’s Message step and every link to your own
              website carries the campaign’s tracking name. Analytics then counts the visits and reservations
              each campaign brought: on the campaign’s Results, and on the Analytics Campaigns card, which
              opens the campaign back up. Links to anybody else’s site go out exactly as written, and texts
              are never tagged — the extra characters would cost a second segment.
            </p>
            <ul className="cmg-guide-checks">
              <li className={hosts.length ? 'is-done' : ''}>
                {hosts.length ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                <span>
                  {tracking.loading && !tracking.data ? (
                    'Looking up your website…'
                  ) : tracking.error ? (
                    'Could not look up your website.'
                  ) : hosts.length ? (
                    <>
                      Your website: <b>{hosts.map((site) => site.host).join(', ')}</b>
                      {hosts[0].from === 'company' ? ', from your company profile' : ', from Analytics'}
                    </>
                  ) : (
                    <>
                      We do not know your website’s address. Add it as the Website on your company profile
                      in the dispatch dashboard, or under Allowed sites on an Analytics install.
                    </>
                  )}
                </span>
              </li>
              <li className={webInstalls.length ? 'is-done' : ''}>
                {webInstalls.length ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                <span>
                  {!analyticsOn ? (
                    'Website analytics is switched off for your role — an administrator can see these numbers.'
                  ) : sites.loading && !sites.data ? (
                    'Looking for an Analytics install…'
                  ) : webInstalls.length ? (
                    reporting ? (
                      'Analytics is counting visits on your site.'
                    ) : (
                      'Analytics is installed but has not reported in the last day — check the tag is on your pages.'
                    )
                  ) : (
                    'Nothing is counting visits yet — add the Analytics tag to your website first.'
                  )}
                </span>
              </li>
            </ul>
            {onOpenAnalytics && analyticsOn ? (
              <button type="button" className="btn btn-secondary" onClick={onOpenAnalytics}>
                Open Analytics
                <ArrowUpRight size={14} />
              </button>
            ) : null}
          </FlowStep>

          {aiOffered && (
            <FlowStep
              n={n('ai')}
              done={aiReady === true}
              title="Let AI draft the copy"
              tag={aiReady ? { label: 'Ready', tone: 'ok' } : { label: 'Optional' }}
            >
              <p>
                In the Message step, <b>AI assist</b> writes a campaign from a one-line brief and polishes
                one you have written — with your company details as data points rather than typed in.
                It runs on an OpenAI or Anthropic key, added once in AI settings.
              </p>
              {ai.loading && !ai.data ? (
                <p className="cmg-guide-list">Checking whether AI is set up…</p>
              ) : aiReady === true ? (
                <p className="cmg-guide-list">AI is set up and ready to use.</p>
              ) : aiReady === false ? (
                mayManageAi ? (
                  <div className="cmg-guide-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => openSettingsTab('/ai', source.embedded)}
                    >
                      <KeyRound size={14} />
                      Set up AI
                      <ArrowUpRight size={14} />
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setAiProbe((p) => p + 1)}>
                      Check again
                    </button>
                  </div>
                ) : (
                  <p className="cmg-guide-list">
                    No AI key is set up yet. Adding one needs AI at Manage — ask whoever looks after your
                    CRM settings.
                  </p>
                )
              ) : null}
            </FlowStep>
          )}

          <FlowStep
            n={n('send')}
            done={Boolean(sent)}
            title="Write it, pick who gets it, send"
            tag={sent ? { label: 'Done', tone: 'ok' } : { label: 'Next' }}
          >
            <p>
              The builder takes four steps. <b>Audience</b>: customers, affiliates or chauffeurs from
              dispatch, narrowed by status, priority or place — or a list you paste. <b>Message</b>: data
              points such as {'{{name}}'} fill in per recipient, and AI assist can draft it from a line.{' '}
              <b>Throttle</b>: how fast it goes out. <b>Review &amp; send</b>: now, or at a time you choose.
              {level === 'draft'
                ? ' Your role submits campaigns for review; somebody who can send approves them.'
                : ''}
            </p>
            {mayDraft ? (
              <div className="cmg-guide-actions">
                <button type="button" className="btn btn-primary" onClick={onNewCampaign}>
                  <Plus size={14} />
                  New campaign
                </button>
                <button type="button" className="btn btn-secondary" onClick={onTryTest}>
                  <FlaskConical size={14} />
                  Try a test campaign first
                </button>
              </div>
            ) : null}
          </FlowStep>

          <FlowStep n={n('results')} title="Read what it did, and follow up" tag={{ label: 'After it sends' }}>
            <p>
              Results shows who was sent to, who opened (on a text, whose phone confirmed it arrived), what
              bounced and who unsubscribed — and, with tracking on, the visits and reservations it brought.
              Resend to the people who did not open, or to the ones that failed, as a new draft. A campaign
              that is sending can be stopped, its wording fixed, and resumed.
            </p>
            {sent ? (
              <button type="button" className="btn btn-secondary" onClick={() => onOpenCampaign(sent)}>
                Open the latest results
              </button>
            ) : null}
          </FlowStep>
        </ol>

        <aside className="an-setup-aside">
          <section className="an-checklist">
            <header className="an-checklist-head">
              <h3>Getting set up</h3>
              <span>
                {doneCount} of {steps.length} done
              </span>
            </header>
            <div
              className="an-checklist-bar"
              role="progressbar"
              aria-label="Campaign setup progress"
              aria-valuemin={0}
              aria-valuemax={steps.length}
              aria-valuenow={doneCount}
            >
              <i style={{ width: `${(doneCount / steps.length) * 100}%` }} />
            </div>
            <ul>
              {steps.map((step) => (
                <li key={step.key}>
                  <button
                    type="button"
                    className={step.done ? 'an-check is-done' : 'an-check'}
                    onClick={() => go(step.key)}
                  >
                    {step.done ? (
                      <CheckCircle2 size={16} className="an-check-icon" />
                    ) : (
                      <Circle size={16} className="an-check-icon" />
                    )}
                    <span className="an-check-text">
                      <span className="an-check-label">{step.label}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="cmg-guide-note">
            <h3>Good to know</h3>
            <ul>
              <li>
                Anyone who unsubscribed or replied STOP is skipped on every campaign, whatever its footer
                says.
              </li>
              <li>A scheduled campaign picks its audience when it goes, so people added before then get it.</li>
              <li>
                {tracked
                  ? 'Reuse a tracking name when you run the same offer again, and its visits add up in one row.'
                  : 'Give an offer you run more than once the same tracking name, and its visits add up in one row.'}
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
